import { TranslateService } from '@ngx-translate/core';
import { ETimeUnit } from 'ngx-mt-ui-lib/enums';
import { declOfNum } from 'ngx-mt-ui-lib/functions';
import { DeclinationTranslation } from 'ngx-mt-ui-lib/translate';
import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';
dayjs.extend(dayjsDuration);

const DURATION_UNIT_KEYS = {
  days: 'DURATION_UNITS.DAYS',
  hours: 'DURATION_UNITS.HOURS',
  minutes: 'DURATION_UNITS.MINUTES',
  seconds: 'DURATION_UNITS.SECONDS',
} as const;

const MS_IN_SECOND = 1000;
const MS_IN_MINUTE = 60_000;
const MS_IN_HOUR = 3_600_000;
const MS_IN_DAY = 86_400_000;

/**
 * Отформатировать таймстамп в человекочитаемую продолжительность с прибл. точностью.
 * @param valueMs время в мс
 * @param translateService сервис переводов
 * @returns
 * - "2 д"
 * - "1 ч"
 * - "30 мин"
 */
export function formatDuration(
  valueMs: number,
  translateService: TranslateService,
): string {
  const absoluteMs = Math.max(0, Math.round(valueMs));

  if (absoluteMs >= MS_IN_DAY) {
    return `${Math.round(absoluteMs / MS_IN_DAY)} ${translateService.instant(
      DURATION_UNIT_KEYS.days,
    )}`;
  }

  if (absoluteMs >= MS_IN_HOUR) {
    return `${Math.round(absoluteMs / MS_IN_HOUR)} ${translateService.instant(
      DURATION_UNIT_KEYS.hours,
    )}`;
  }

  if (absoluteMs >= MS_IN_MINUTE) {
    return `${Math.max(
      1,
      Math.round(absoluteMs / MS_IN_MINUTE),
    )} ${translateService.instant(DURATION_UNIT_KEYS.minutes)}`;
  }

  return `${Math.max(
    1,
    Math.round(absoluteMs / MS_IN_SECOND),
  )} ${translateService.instant(DURATION_UNIT_KEYS.seconds)}`;
}

/**
 * Отформатировать таймстамп в человекочитаемую продолжительность,
 * например, "1 Час, 20 Минут"
 * @param duration значение
 * @param unit единица измерения
 * @param translations переводы
 *
 * @param startWith начальная единица измерения
 * @default ETimeUnit.YEAR
 *
 * @param endWith конечная единица измерения
 * @default ETimeUnit.SECOND
 *
 * @param rounding округление
 * @default 2
 * - 1 Час, 20 Минут
 * - 2 Года, 1 Месяц
 */
export const formatDurationPrecisely = (
  duration: number,
  unit: dayjsDuration.DurationUnitType,
  translations: Record<keyof typeof ETimeUnit, DeclinationTranslation>,
  startWith: ETimeUnit = ETimeUnit.YEAR,
  endWith: ETimeUnit = ETimeUnit.SECOND,
  rounding: number = 2,
): string => {
  const djs = dayjs.duration(duration, unit);
  const parts: string[] = [];

  const config: {
    value: number;
    totalValue: number;
    timeUnit: ETimeUnit;
    translations: DeclinationTranslation;
  }[] = [
    {
      value: Math.floor(djs.years()),
      totalValue: Math.floor(djs.asYears()),
      timeUnit: ETimeUnit.YEAR,
      translations: translations.YEAR,
    },
    {
      value: Math.floor(djs.months()),
      totalValue: Math.floor(djs.asMonths()),
      timeUnit: ETimeUnit.MONTH,
      translations: translations.MONTH,
    },
    {
      value: Math.floor(djs.days()),
      totalValue: Math.floor(djs.asDays()),
      timeUnit: ETimeUnit.DAY,
      translations: translations.DAY,
    },
    {
      value: Math.floor(djs.hours()),
      totalValue: Math.floor(djs.asHours()),
      timeUnit: ETimeUnit.HOUR,
      translations: translations.HOUR,
    },
    {
      value: Math.floor(djs.minutes()),
      totalValue: Math.floor(djs.asMinutes()),
      timeUnit: ETimeUnit.MINUTE,
      translations: translations.MINUTE,
    },
    {
      value: Math.floor(djs.seconds()),
      totalValue: Math.floor(djs.asSeconds()),
      timeUnit: ETimeUnit.SECOND,
      translations: translations.SECOND,
    },
  ];

  let skip = true;
  for (const c of config) {
    if (startWith === c.timeUnit) {
      skip = false;
    }

    if (skip) continue;

    const value = startWith === c.timeUnit ? c.totalValue : c.value;

    if (!value) continue;

    const decl = declOfNum(value, Object.values(c.translations));
    parts.push(`${value} ${decl}`);

    if (endWith === c.timeUnit) break;
  }

  return parts.slice(0, rounding).join(', ');
};

export function formatDurationForMetroTrackClosure(
  valueMs: number,
  translateService: TranslateService,
): string {
  let result = '';
  const days = Math.floor(valueMs / MS_IN_DAY);
  const hours = Math.floor((valueMs % MS_IN_DAY) / MS_IN_HOUR);
  const minutes = Math.floor((valueMs % MS_IN_HOUR) / MS_IN_MINUTE);
  const seconds = Math.floor((valueMs % MS_IN_MINUTE) / MS_IN_SECOND);
  if (days > 0) {
    result = `${days}${translateService.instant(DURATION_UNIT_KEYS.days)} `;
  }
  result += hours < 10 ? `0${hours}:` : `${hours}:`;
  result += minutes < 10 ? `0${minutes}:` : `${minutes}:`;
  result += seconds < 10 ? `0${seconds}` : `${seconds}`;
  return result;
}

export function formatDurationForMassEvent(
  valueMs: number,
  translateService: TranslateService,
): string {
  let result = '';
  const days = Math.floor(valueMs / MS_IN_DAY);
  const hours = Math.floor((valueMs % MS_IN_DAY) / MS_IN_HOUR);
  const minutes = Math.round((valueMs % MS_IN_HOUR) / MS_IN_MINUTE);
  if (days > 0) {
    result = `${days}${translateService.instant(DURATION_UNIT_KEYS.days)} `;
  }
  result +=
    hours > 0
      ? `${hours}${translateService.instant(DURATION_UNIT_KEYS.hours)} `
      : ``;
  result +=
    minutes > 0
      ? `${minutes}${translateService.instant(DURATION_UNIT_KEYS.minutes)}`
      : ``;
  return result;
}

export function calcDuration(
  startDate: string,
  endDate: string = null,
): number {
  let duration = 0;
  if (startDate) {
    const startMoment = dayjs(startDate);
    const endMoment = endDate ? dayjs(endDate) : dayjs();
    duration = endMoment.diff(startMoment);
  }
  return duration;
}
